import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import GuardianRuleCard, { EVIDENCE_LABEL, STATUS_LABELS } from "@/components/guardian/GuardianRuleCard";
import "@/components/guardian/guardian.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/platform/guardian/catalog`;
const text = (value) => typeof value === "string" && value.trim().length > 0;
const noEvidence = (value) => value?.state === "not_connected" &&
  value.checked_revision === null && value.checked_at === null;

function validCatalog(data) {
  if (!data || data.schema_version !== 1 || data.source?.repository !== "jfm1-has/Jfm" ||
      !text(data.source.rulebook_version) || !noEvidence(data.evidence) ||
      !Array.isArray(data.rules) || data.rules.length === 0 ||
      data.summary?.total_rules !== data.rules.length || !Array.isArray(data.warnings)) return false;
  if (data.source.revision !== null && !/^[a-f0-9]{40}$/.test(data.source.revision || "")) return false;
  if (data.warnings.some(w => !w || !text(w.message))) return false;
  const ids = new Set();
  const counts = Object.fromEntries(Object.keys(STATUS_LABELS).map(key => [key, 0]));
  for (const rule of data.rules) {
    if (!rule || !/^JFM-[A-Z]+-\d+$/.test(rule.id || "") || ids.has(rule.id) ||
        !["title", "meaning", "why_it_matters", "recommendation", "category"].every(key => text(rule[key])) ||
        !Object.prototype.hasOwnProperty.call(STATUS_LABELS, rule.recorded_status) ||
        !["current", "superseded", "deferred"].includes(rule.applicability) ||
        !["present", "missing"].includes(rule.explanation_state) ||
        (rule.applicability_note !== null && typeof rule.applicability_note !== "string") ||
        !noEvidence(rule.evidence) || !Array.isArray(rule.listed_tests) ||
        rule.listed_tests.some(path => !text(path) || /(^\/|\\|:|(^|\/)\.\.(\/|$))/.test(path))) return false;
    const ai = rule.id.startsWith("JFM-AI-");
    if (rule.source_path !== (ai ? "JFM_ACTION_AI_ASSISTANT_RULES.md" : "JFM_GUARDIAN_RULES.md") ||
        (ai && rule.category !== "Action AI Assistant")) return false;
    ids.add(rule.id);
    counts[rule.recorded_status] += 1;
  }
  return Object.keys(counts).every(key => data.summary.by_status?.[key] === counts[key]);
}

export default function PlatformGuardianPage() {
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [denied, setDenied] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [view, setView] = useState("overview");
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setLoading(true); setCatalog(null); setError(""); setDenied(false);
    axios.get(API, { signal: controller.signal }).then(({ data }) => {
      if (!active) return;
      if (!validCatalog(data)) throw new Error("Invalid guide");
      setCatalog(data);
    }).catch((failure) => {
      if (!active) return;
      setCatalog(null);
      const noAccess = [401, 403].includes(failure?.response?.status);
      setDenied(noAccess);
      setError(noAccess ? "Platform Owner access is required. Check your sign-in and any required password change."
        : "Guardian guide could not be loaded. Try again or contact support.");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [attempt]);

  const categories = useMemo(() => [...new Set((catalog?.rules || []).map(rule => rule.category))].sort(), [catalog]);
  const rules = useMemo(() => (catalog?.rules || []).filter(rule => {
    const search = query.trim().toLowerCase();
    return (!category || rule.category === category) &&
      (!status || (status === "attention" ? rule.recorded_status !== "enforced" && rule.applicability === "current" : rule.recorded_status === status)) &&
      (!search || [rule.id, rule.title, rule.meaning].some(value => value.toLowerCase().includes(search)));
  }), [catalog, category, query, status]);

  if (loading) return <section className="guardian-page" role="status">Loading Guardian guide…</section>;
  if (error) return <section className="guardian-page">
    <p role="alert">{error}</p>
    {!denied && <button type="button" onClick={() => setAttempt(value => value + 1)}>Try again</button>}
  </section>;

  return (
    <section className="guardian-page" aria-labelledby="guardian-heading">
      <header>
        <p className="guardian-kicker">Platform Owner · Read-only</p>
        <h2 id="guardian-heading">Guardian</h2>
        <p>Understand JFM’s rules, what they protect, and what needs a closer look.</p>
        <p className="guardian-muted">Guide version {catalog.source.rulebook_version}</p>
      </header>
      <nav aria-label="Guardian views" className="guardian-tabs">
        <button type="button" aria-pressed={view === "overview"} onClick={() => setView("overview")}>Overview</button>
        <button type="button" aria-pressed={view === "rules"} onClick={() => setView("rules")}>Rules</button>
      </nav>
      {catalog.warnings.length > 0 && <div role="alert" className="guardian-notice">
        {catalog.warnings.map((warning, index) => <p key={index}>{warning.message}</p>)}
      </div>}
      {view === "overview" ? (
        <div>
          <div className="guardian-notice">
            <strong>{EVIDENCE_LABEL}</strong>
            <p>This guide shows recorded requirements and implementation claims. It does not certify a release or say every listed test passed.</p>
          </div>
          <div className="guardian-counts">
            <div><strong>{catalog.summary.total_rules}</strong><span>Registered rules</span></div>
            {Object.entries(STATUS_LABELS).map(([key, label]) => <div key={key}>
              <strong>{catalog.summary.by_status[key]}</strong><span>{label}</span>
            </div>)}
          </div>
          <section className="guardian-next">
            <h3>Your next step</h3>
            <p>Start with current rules whose protection is recorded as incomplete. Deferred and superseded rules remain available in the full guide.</p>
            <button type="button" onClick={() => { setView("rules"); setStatus("attention"); setCategory(""); setQuery(""); }}>Review rules needing attention</button>
          </section>
          <section className="guardian-links">
            <h3>Existing platform checks</h3>
            <a href="/platform/production-health">Runtime Health</a><p>Service health observations, with their own scope and timestamps.</p>
            <a href="/platform/access-studio">Access Studio</a><p>Who can access each area. Access verification is separate from this business-rule guide.</p>
          </section>
          <p className="guardian-muted">Release results and deployed versions are not connected here yet. Source revision: {catalog.source.revision || "unavailable"}.</p>
        </div>
      ) : (
        <div>
          <p>Recorded status describes the registry’s claim. Expand a rule for its meaning, recommendation and evidence details.</p>
          <div className="guardian-filters">
            <label>Search rules<input value={query} onChange={event => setQuery(event.target.value)} placeholder="Rule ID, title or meaning" type="search" /></label>
            <label>Category<select value={category} onChange={event => setCategory(event.target.value)}>
              <option value="">All categories</option>{categories.map(value => <option key={value} value={value}>{value}</option>)}
            </select></label>
            <label>Recorded status<select value={status} onChange={event => setStatus(event.target.value)}>
              <option value="">All statuses</option><option value="attention">Current rules to review</option>
              {Object.entries(STATUS_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
            </select></label>
          </div>
          <p role="status">{rules.length} of {catalog.summary.total_rules} rules</p>
          {rules.length ? rules.map(rule => <GuardianRuleCard key={rule.id} rule={rule} source={catalog.source} />) : <p>No rules match your search.</p>}
        </div>
      )}
    </section>
  );
}
